import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createDemoComparisonRows, createDemoParsedSheet } from '../demo'
import {
  applyManualDecisionToResults,
  matchBoqRows,
  undoManualDecisionInResults,
} from '../domain/matching'
import { emptyColumnMapping } from '../domain/parsing'
import { buildReportData } from '../domain/report'
import {
  createBoqParseWorkerClient,
  detectSheetMapping,
  mapSheetToBoqRows,
  type BoqParseWorkerClient,
  type ImportedWorkbookMetadata,
  type ImportProgress,
  type MappedBoqSheet,
  type ParsedSheet,
} from '../io'
import type {
  ColumnMapping,
  ColumnMappingField,
  DiffSummary,
  ManualDecision,
  MatchResult,
  SourceType,
} from '../types'
import {
  loadComparisonSettings,
  normalizeComparisonSettings,
  saveComparisonSettings,
  type ComparisonSettings,
} from '../ui/settings'
import { summarizeDiffResults } from '../domain/diff'

export interface SourceWorkspaceState {
  sourceType: SourceType
  metadata: ImportedWorkbookMetadata | null
  selectedSheet: string
  mapping: ColumnMapping
  parsed: ParsedSheet | null
  mapped: MappedBoqSheet | null
  busy: boolean
  error: string | null
  progress: ImportProgress | null
  isDemo: boolean
}

export type WorkspaceSources = Record<SourceType, SourceWorkspaceState>

function emptySource(sourceType: SourceType): SourceWorkspaceState {
  return {
    sourceType,
    metadata: null,
    selectedSheet: '',
    mapping: emptyColumnMapping(),
    parsed: null,
    mapped: null,
    busy: false,
    error: null,
    progress: null,
    isDemo: false,
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : '处理文件时发生未知错误，请重试。'
}

function demoMetadata(sourceType: SourceType, parsed: ParsedSheet): ImportedWorkbookMetadata {
  return {
    workbookId: parsed.workbookId,
    sourceType,
    fileName: parsed.fileName,
    fileSize: parsed.fileSize,
    format: parsed.format,
    sheetCount: 1,
    sheets: [parsed.sheet],
    warnings: [],
  }
}

function mappingMatches(left: ColumnMapping, right: ColumnMapping): boolean {
  return Object.keys(left).every(
    (key) => left[key as keyof ColumnMapping] === right[key as keyof ColumnMapping],
  )
}

export interface UseBoqWorkspaceResult {
  sources: WorkspaceSources
  settings: ComparisonSettings
  setSettings: (settings: ComparisonSettings) => void
  results: readonly MatchResult[]
  summary: DiffSummary | null
  comparing: boolean
  exporting: boolean
  comparedAt: Date | null
  hasUnsavedResults: boolean
  lastDecidedId: string | null
  loadFile: (sourceType: SourceType, file: File) => Promise<void>
  clearSource: (sourceType: SourceType) => Promise<void>
  selectSheet: (sourceType: SourceType, sheetName: string) => Promise<void>
  setHeaderRow: (sourceType: SourceType, row: number) => Promise<void>
  setMappingField: (
    sourceType: SourceType,
    field: ColumnMappingField,
    column: number | null,
  ) => void
  loadDemo: () => void
  compare: () => Promise<readonly MatchResult[]>
  applyDecision: (resultId: string, decision: ManualDecision) => void
  undoDecision: (resultId: string) => void
  exportReport: () => Promise<string>
  clearAll: () => Promise<void>
}

export function useBoqWorkspace(): UseBoqWorkspaceResult {
  const clientRef = useRef<BoqParseWorkerClient | null>(null)
  const [sources, setSources] = useState<WorkspaceSources>({
    baseline: emptySource('baseline'),
    target: emptySource('target'),
  })
  const [settingsState, setSettingsState] = useState(loadComparisonSettings)
  const [results, setResults] = useState<MatchResult[]>([])
  const [comparing, setComparing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [comparedAt, setComparedAt] = useState<Date | null>(null)
  const [hasUnsavedResults, setHasUnsavedResults] = useState(false)
  const [lastDecidedId, setLastDecidedId] = useState<string | null>(null)

  const invalidateResults = useCallback((): void => {
    setResults([])
    setComparedAt(null)
    setHasUnsavedResults(false)
    setLastDecidedId(null)
  }, [])

  const ensureClient = useCallback((): BoqParseWorkerClient => {
    clientRef.current ??= createBoqParseWorkerClient()
    return clientRef.current
  }, [])

  const updateSource = useCallback(
    (sourceType: SourceType, update: Partial<SourceWorkspaceState>): void => {
      setSources((current) => ({
        ...current,
        [sourceType]: { ...current[sourceType], ...update },
      }))
    },
    [],
  )

  const progressHandler = useCallback(
    (sourceType: SourceType) =>
      (progress: ImportProgress): void => {
        updateSource(sourceType, { progress })
      },
    [updateSource],
  )

  const parseSelectedSheet = useCallback(
    async (
      sourceType: SourceType,
      metadata: ImportedWorkbookMetadata,
      sheetName: string,
      requestedHeaderRow?: number,
    ): Promise<{ parsed: ParsedSheet; mapping: ColumnMapping }> => {
      const sheet = metadata.sheets.find((item) => item.name === sheetName)
      if (!sheet) throw new Error('找不到所选工作表，请重新选择文件。')
      const detection = detectSheetMapping(sheet)
      const headerRow =
        requestedHeaderRow ?? detection?.headerRow ?? sheet.firstNonEmptyRowNumber ?? 1
      const parsed = await ensureClient().parseSheet(
        metadata.workbookId,
        sheetName,
        { headerRow },
        { onProgress: progressHandler(sourceType) },
      )
      const mapping =
        requestedHeaderRow === undefined && detection?.headerRow === headerRow
          ? detection.mapping
          : (detectSheetMapping({
              ...sheet,
              scanRows: [
                {
                  sourceRowNumber: headerRow,
                  cells: parsed.headerCells,
                  nonEmptyCellCount: parsed.headerCells.filter(
                    (cell) => cell.displayText.trim().length > 0,
                  ).length,
                },
              ],
              firstNonEmptyRowNumber: headerRow,
              lastNonEmptyRowNumber: headerRow,
            })?.mapping ?? emptyColumnMapping(headerRow))
      return { parsed, mapping: { ...mapping, headerRow } }
    },
    [ensureClient, progressHandler],
  )

  const loadFile = useCallback(
    async (sourceType: SourceType, file: File): Promise<void> => {
      invalidateResults()
      updateSource(sourceType, { busy: true, error: null, progress: null })
      try {
        const previous = sources[sourceType].metadata
        if (previous && !sources[sourceType].isDemo) {
          await ensureClient().releaseWorkbook(previous.workbookId)
        }
        const metadata = await ensureClient().loadFile(file, sourceType, {
          onProgress: progressHandler(sourceType),
        })
        if (metadata.sheets.length === 0) throw new Error('工作簿中没有可读取的工作表。')
        const firstSheet =
          metadata.sheets.find((sheet) => sheet.visibility === 'visible') ?? metadata.sheets[0]
        const { parsed, mapping } = await parseSelectedSheet(sourceType, metadata, firstSheet.name)
        updateSource(sourceType, {
          metadata,
          selectedSheet: firstSheet.name,
          mapping,
          parsed,
          mapped: null,
          busy: false,
          error: null,
          isDemo: false,
        })
      } catch (error) {
        updateSource(sourceType, { busy: false, error: errorMessage(error) })
      }
    },
    [ensureClient, invalidateResults, parseSelectedSheet, progressHandler, sources, updateSource],
  )

  const clearSource = useCallback(
    async (sourceType: SourceType): Promise<void> => {
      const metadata = sources[sourceType].metadata
      if (metadata && !sources[sourceType].isDemo && clientRef.current) {
        await clientRef.current.releaseWorkbook(metadata.workbookId).catch(() => false)
      }
      setSources((current) => ({ ...current, [sourceType]: emptySource(sourceType) }))
      invalidateResults()
    },
    [invalidateResults, sources],
  )

  const selectSheet = useCallback(
    async (sourceType: SourceType, sheetName: string): Promise<void> => {
      const source = sources[sourceType]
      if (!source.metadata) return
      invalidateResults()
      updateSource(sourceType, { busy: true, selectedSheet: sheetName, error: null })
      try {
        const { parsed, mapping } = await parseSelectedSheet(sourceType, source.metadata, sheetName)
        updateSource(sourceType, { busy: false, parsed, mapping, mapped: null })
      } catch (error) {
        updateSource(sourceType, { busy: false, error: errorMessage(error) })
      }
    },
    [invalidateResults, parseSelectedSheet, sources, updateSource],
  )

  const setHeaderRow = useCallback(
    async (sourceType: SourceType, row: number): Promise<void> => {
      const source = sources[sourceType]
      invalidateResults()
      if (!source.metadata || !source.selectedSheet || source.isDemo) {
        updateSource(sourceType, { mapping: { ...source.mapping, headerRow: row }, mapped: null })
        return
      }
      updateSource(sourceType, { busy: true, error: null })
      try {
        const { parsed, mapping } = await parseSelectedSheet(
          sourceType,
          source.metadata,
          source.selectedSheet,
          row,
        )
        updateSource(sourceType, { busy: false, parsed, mapping, mapped: null })
      } catch (error) {
        updateSource(sourceType, { busy: false, error: errorMessage(error) })
      }
    },
    [invalidateResults, parseSelectedSheet, sources, updateSource],
  )

  const setMappingField = useCallback(
    (sourceType: SourceType, field: ColumnMappingField, column: number | null): void => {
      invalidateResults()
      setSources((current) => ({
        ...current,
        [sourceType]: {
          ...current[sourceType],
          mapping: { ...current[sourceType].mapping, [field]: column },
          mapped: null,
        },
      }))
    },
    [invalidateResults],
  )

  const loadDemo = useCallback((): void => {
    const demo = createDemoComparisonRows()
    const baselineParsed = createDemoParsedSheet('baseline')
    const targetParsed = createDemoParsedSheet('target')
    setSources({
      baseline: {
        sourceType: 'baseline',
        metadata: demoMetadata('baseline', baselineParsed),
        selectedSheet: baselineParsed.sheet.name,
        mapping: demo.baseline.mapping,
        parsed: baselineParsed,
        mapped: demo.baseline,
        busy: false,
        error: null,
        progress: null,
        isDemo: true,
      },
      target: {
        sourceType: 'target',
        metadata: demoMetadata('target', targetParsed),
        selectedSheet: targetParsed.sheet.name,
        mapping: demo.target.mapping,
        parsed: targetParsed,
        mapped: demo.target,
        busy: false,
        error: null,
        progress: null,
        isDemo: true,
      },
    })
    invalidateResults()
  }, [invalidateResults])

  const mappedSource = useCallback(
    async (source: SourceWorkspaceState): Promise<MappedBoqSheet> => {
      if (!source.metadata || !source.parsed || !source.selectedSheet) {
        throw new Error('请先导入文件并选择工作表。')
      }
      if (source.mapped && mappingMatches(source.mapped.mapping, source.mapping))
        return source.mapped
      if (source.isDemo) return mapSheetToBoqRows(source.parsed, source.mapping)
      return ensureClient().parseMappedSheet(
        source.metadata.workbookId,
        source.selectedSheet,
        source.mapping,
        { onProgress: progressHandler(source.sourceType) },
      )
    },
    [ensureClient, progressHandler],
  )

  const compare = useCallback(async (): Promise<readonly MatchResult[]> => {
    setComparing(true)
    try {
      const [baseline, target] = await Promise.all([
        mappedSource(sources.baseline),
        mappedSource(sources.target),
      ])
      const compared = matchBoqRows(baseline.rows, target.rows, {
        ...settingsState,
        maxDisplayedCandidates: 3,
      })
      setSources((current) => ({
        baseline: { ...current.baseline, mapped: baseline },
        target: { ...current.target, mapped: target },
      }))
      setResults(compared)
      setComparedAt(new Date())
      setHasUnsavedResults(true)
      setLastDecidedId(null)
      return compared
    } finally {
      setComparing(false)
    }
  }, [mappedSource, settingsState, sources])

  const applyDecision = useCallback(
    (resultId: string, decision: ManualDecision): void => {
      setResults((current) =>
        applyManualDecisionToResults(current, resultId, decision, settingsState.tolerances),
      )
      setLastDecidedId(resultId)
      setHasUnsavedResults(true)
    },
    [settingsState.tolerances],
  )

  const undoDecision = useCallback(
    (resultId: string): void => {
      setResults((current) =>
        undoManualDecisionInResults(current, resultId, settingsState.tolerances),
      )
      setLastDecidedId(null)
      setHasUnsavedResults(true)
    },
    [settingsState.tolerances],
  )

  const exportReport = useCallback(async (): Promise<string> => {
    const baseline = sources.baseline
    const target = sources.target
    if (!baseline.mapped || !target.mapped || !comparedAt || results.length === 0) {
      throw new Error('当前没有可导出的对比结果。')
    }
    setExporting(true)
    try {
      const data = buildReportData({
        baseline: {
          fileName: baseline.mapped.fileName,
          sheetName: baseline.mapped.sheetName,
          columnMapping: baseline.mapping,
        },
        target: {
          fileName: target.mapped.fileName,
          sheetName: target.mapped.sheetName,
          columnMapping: target.mapping,
        },
        results,
        excludedRows: [...baseline.mapped.excludedRows, ...target.mapped.excludedRows],
        tolerances: settingsState.tolerances,
        matchingOptions: settingsState,
        comparedAt,
      })
      const { downloadDiffReport, exportDiffReport } = await import('../domain/export')
      const report = await exportDiffReport(data)
      downloadDiffReport(report)
      setHasUnsavedResults(false)
      return report.fileName
    } finally {
      setExporting(false)
    }
  }, [comparedAt, results, settingsState, sources])

  const clearAll = useCallback(async (): Promise<void> => {
    if (clientRef.current) await clientRef.current.clear().catch(() => undefined)
    setSources({ baseline: emptySource('baseline'), target: emptySource('target') })
    invalidateResults()
  }, [invalidateResults])

  const setSettings = useCallback(
    (settings: ComparisonSettings): void => {
      const normalized = normalizeComparisonSettings(settings)
      setSettingsState(normalized)
      saveComparisonSettings(normalized)
      // Existing results were calculated with the previous parameters and must not be exported
      // under a different settings snapshot.
      invalidateResults()
    },
    [invalidateResults],
  )

  useEffect(() => {
    if (!hasUnsavedResults) return
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedResults])

  useEffect(
    () => () => {
      clientRef.current?.dispose()
    },
    [],
  )

  const summary = useMemo(
    () => (results.length > 0 ? summarizeDiffResults(results) : null),
    [results],
  )

  return {
    sources,
    settings: settingsState,
    setSettings,
    results,
    summary,
    comparing,
    exporting,
    comparedAt,
    hasUnsavedResults,
    lastDecidedId,
    loadFile,
    clearSource,
    selectSheet,
    setHeaderRow,
    setMappingField,
    loadDemo,
    compare,
    applyDecision,
    undoDecision,
    exportReport,
    clearAll,
  }
}
